/**
 * b15b U4 — Raid Radar dex cross-reference.
 *
 * Given a raid event's boosted spawn list and the trainer's collection foci,
 * work out which (species, variant) pairs the trainer is still MISSING — so the
 * Profile Raid Radar can render a picture-button per gap that deep-links to that
 * variant's Pokédex entry. "Don't waste a raid on something you already have;
 * here's the shiny/lucky/perfect you still need from this boss."
 *
 * Pure + storage-agnostic: the caller passes a `getPresent(variant, id)`
 * predicate (wired to dexStateStore in the component) so this stays unit-
 * testable without a DOM. Mirrors the eventDexSummary.ts adapter pattern.
 */
import type { CollectionFocus } from "./userPriorities";
import { isVariantAvailable } from "./variantAvailability";
import { raidBossFormId } from "./raidRoster";

/**
 * Is this event a Max Battle event (Max Monday included)? The feed classifies
 * both as their own categories ("max-battle", "max-monday"); rawType rides
 * along as a fallback for older cached feeds. A Max event featuring a species
 * IS a Dynamax chase by definition, so the cross-reference implies a Dynamax
 * gap for its spawns without waiting on an admin to tick the D-MAX chip: the
 * exact mirror of shinyBoost pulling Shiny in regardless of foci.
 */
export function isMaxBattleEventCategory(
  category?: string | null,
  rawType?: string | null,
): boolean {
  const has = (s?: string | null) => (s ?? "").toLowerCase().includes("max");
  return has(category) || has(rawType);
}

/**
 * Variants the raid cross-reference can flag a gap in — the full scannable dex
 * set, so an admin event-link in any variant (a Shadow raid, a Mega battle …)
 * can surface as a "you're missing this" gap. `getPresent` is wired to the dex
 * store, which knows every one of these. The foci-derived fallback
 * (`variantsForTrainer`) still only auto-checks the four whole-species variants.
 */
export type RaidDexVariant =
  | "pokemon"
  | "shiny"
  | "lucky"
  | "perfect"
  | "xxl"
  | "xxs"
  | "mega"
  | "gmax"
  | "dynamax"
  | "shadow"
  | "purified";

export interface RaidSpawnInput {
  speciesId?: number;
  name?: string;
  shinyBoost?: boolean;
  /**
   * Explicit variants this species applies in for the event, from an admin
   * event-link override. When present it DRIVES the cross-reference — the exact
   * variants the admin chose are checked — instead of deriving them from the
   * trainer's foci. Absent for auto-detected spawns, which keep the foci path.
   */
  variants?: RaidDexVariant[];
  /**
   * Specific forms/costumes of the species the event features (admin
   * event-link override, migration 0078). Each becomes its own form-scoped
   * gap. When forms are present and `variants` is empty, the admin linked
   * ONLY the forms — no species-level gap is derived from foci.
   */
  forms?: Array<{ formId: string; variants?: RaidDexVariant[] }>;
  /**
   * The FORM an auto-detected feed spawn names, as the registry suffix
   * ("armored", "galar"; worker feedFormSuffix, 2026-09-08). Resolved here to
   * the form id (raidBossFormId) and the spawn's gaps are scoped to that form,
   * with the trainer's foci variants: an Armored Mewtwo raid is an Armored
   * chase, never a plain-Mewtwo one. Ignored when admin `forms` are present.
   */
  formSuffix?: string;
}

export interface RaidDexGap {
  speciesId: number;
  name: string | undefined;
  /** The variant the trainer is missing this species in. */
  variant: RaidDexVariant;
  /** Set for a form-scoped gap: the missing copy is that FORM of the species
   * (a costume Pikachu), checked against the form-level dex row. */
  formId?: string;
}

/**
 * Decide which variants to cross-reference for a given trainer. Main is always
 * checked (a raid is a catch). Shiny/lucky/perfect are added when the trainer's
 * foci say they chase them, so a pure-shiny hunter isn't nagged about luckies.
 *
 * `shinyBoost` on a spawn always pulls shiny in regardless of foci — a
 * shiny-boosted raid is the prime moment to fill a shiny gap.
 */
export function variantsForTrainer(
  foci: readonly CollectionFocus[],
  spawnIsShinyBoosted: boolean,
): RaidDexVariant[] {
  const out: RaidDexVariant[] = ["pokemon"];
  const wantsShiny =
    spawnIsShinyBoosted ||
    foci.includes("shiny_dex") ||
    foci.includes("shundo");
  const wantsLucky = foci.includes("lucky");
  const wantsPerfect = foci.includes("hundos") || foci.includes("shundo");
  if (wantsShiny) out.push("shiny");
  if (wantsLucky) out.push("lucky");
  if (wantsPerfect) out.push("perfect");
  return out;
}

/**
 * Cross-reference a raid event's spawns against the trainer's local dex state.
 * Returns one gap per (species, variant) the trainer is missing, in spawn order
 * then variant order (main, shiny, lucky, perfect).
 *
 * `getPresent` returns true when the trainer already has that species in that
 * variant — the component wires it to dexStateStore's per-variant present set.
 */
export function raidDexGaps(
  spawns: readonly RaidSpawnInput[],
  foci: readonly CollectionFocus[],
  getPresent: (variant: RaidDexVariant, speciesId: number, formId?: string) => boolean,
  opts?: {
    /** Max Battle / Max Monday event: every featured species in the Dynamax
     * roster also gets a Dynamax gap check, admin D-MAX tick or not. */
    impliedDynamax?: boolean;
  },
): RaidDexGap[] {
  const gaps: RaidDexGap[] = [];
  const seen = new Set<string>();
  for (const sp of spawns) {
    const id = sp.speciesId;
    if (typeof id !== "number" || !Number.isFinite(id) || id < 1) continue;
    const feedFormId =
      (!sp.forms || sp.forms.length === 0) && sp.formSuffix
        ? raidBossFormId({ speciesId: id, formSuffix: sp.formSuffix })
        : null;
    // A feed spawn that names a form is scoped to it like an admin form link,
    // but keeps the foci path (Main + Shiny plus whatever the trainer chases),
    // since nobody curated its variants.
    const forms: Array<{ formId: string; variants?: RaidDexVariant[] }> = feedFormId
      ? [{
          formId: feedFormId,
          variants: Array.from(new Set<RaidDexVariant>([
            "pokemon",
            "shiny",
            ...variantsForTrainer(foci, sp.shinyBoost === true),
          ])),
        }]
      : (sp.forms ?? []);
    // An admin override's explicit variants win over the foci-derived set: the
    // curator has said exactly which variants this event features for this
    // species, so honour them verbatim. Auto-detected spawns carry none and
    // fall back to the trainer's foci as before — UNLESS the spawn carries
    // form links, where empty species variants mean the curator scoped the
    // event to the forms alone (a costume event features the costume, not a
    // base-dex Pikachu chase).
    const variants =
      sp.variants && sp.variants.length > 0
        ? sp.variants
        : forms.length > 0
          ? []
          : variantsForTrainer(foci, sp.shinyBoost === true);
    for (const variant of variants) {
      const key = `${variant}:${id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      if (getPresent(variant, id)) continue; // already have it — not a gap
      gaps.push({ speciesId: id, name: sp.name, variant });
    }
    // A Max event implies the Dynamax chase for every featured species that
    // can actually Dynamax (curated roster). Runs after the explicit variants
    // so an admin's own D-MAX tick is never doubled, and the roster gate keeps
    // a mislabelled spawn from offering a Dynamax nothing can have.
    if (opts?.impliedDynamax && isVariantAvailable("dynamax", id)) {
      const key = `dynamax:${id}`;
      if (!seen.has(key)) {
        seen.add(key);
        if (!getPresent("dynamax", id)) {
          gaps.push({ speciesId: id, name: sp.name, variant: "dynamax" });
        }
      }
    }
    // Form-scoped gaps: one per (form, variant), checked against the FORM's
    // own dex row. A curator-scoped form defaults to Main + Shiny when it
    // carries no explicit variants (the costume-event norm).
    for (const form of forms) {
      if (!form.formId) continue;
      const formVariants =
        form.variants && form.variants.length > 0 ? form.variants : (["pokemon", "shiny"] as const);
      for (const variant of formVariants) {
        const key = `${variant}:${id}:${form.formId}`;
        if (seen.has(key)) continue;
        seen.add(key);
        if (getPresent(variant, id, form.formId)) continue;
        gaps.push({ speciesId: id, name: sp.name, variant, formId: form.formId });
      }
    }
  }
  return gaps;
}

/**
 * Canonical display order for gap variants — the same order the dex variant
 * pills and badges use (DEX_VARIANT_META), so "Main, Shiny, Lucky, 100%, …"
 * reads identically wherever variants are listed.
 */
export const RAID_VARIANT_DISPLAY_ORDER: readonly RaidDexVariant[] = [
  "pokemon", "shiny", "lucky", "perfect", "xxs", "xxl",
  "mega", "gmax", "dynamax", "shadow", "purified",
];

const VARIANT_RANK = new Map<RaidDexVariant, number>(
  RAID_VARIANT_DISPLAY_ORDER.map((v, i) => [v, i]),
);

/**
 * Re-order gaps so every gap in the SAME variant sits together (all the shinies,
 * then all the luckies …), keeping the original spawn order inside each group.
 *
 * raidDexGaps emits in spawn order — species A's main + shiny, then species B's
 * main + shiny — which interleaves variants and makes a strip of a dozen tiles
 * read as noise. Grouping by variant turns it into "here's what you're missing,
 * by dex". Stable: a plain `sort` on the ranked pairs would not be, and the
 * within-variant order is the event's own species order.
 */
export function sortGapsByVariant(gaps: readonly RaidDexGap[]): RaidDexGap[] {
  const rank = (v: RaidDexVariant) => VARIANT_RANK.get(v) ?? RAID_VARIANT_DISPLAY_ORDER.length;
  return gaps
    .map((gap, index) => ({ gap, index }))
    .sort((a, b) => rank(a.gap.variant) - rank(b.gap.variant) || a.index - b.index)
    .map((e) => e.gap);
}

/**
 * The same gaps, bucketed into one group per variant, in canonical variant
 * order. Empty variants are omitted, so the caller renders only the groups that
 * actually have something missing.
 */
export function groupGapsByVariant(
  gaps: readonly RaidDexGap[],
): { variant: RaidDexVariant; gaps: RaidDexGap[] }[] {
  const groups = new Map<RaidDexVariant, RaidDexGap[]>();
  for (const gap of sortGapsByVariant(gaps)) {
    const bucket = groups.get(gap.variant);
    if (bucket) bucket.push(gap);
    else groups.set(gap.variant, [gap]);
  }
  return [...groups.entries()].map(([variant, list]) => ({ variant, gaps: list }));
}

/**
 * Trim `gaps` to `limit` WITHOUT losing a variant.
 *
 * The strip used to sort by variant and then slice, on the reasoning that a
 * truncated strip should drop whole tail variants rather than half of every
 * group. That reasoning fails the case it matters most in: an admin links a
 * Max Monday to nine Eeveelutions in Shiny + 100% + D-MAX, which is 27 gaps,
 * and a flat 12-slice keeps 9 shiny + 3 perfect and shows the trainer NO D-MAX
 * at all. The linked variant the event is actually named for vanishes, and the
 * strip looks like the link never happened (2026-08-31, owner report).
 *
 * So the cap is now shared ROUND-ROBIN across the variant groups: one gap to
 * each group, then a second to each, until the budget runs out. Every linked
 * variant is represented before any variant gets a second helping, a group
 * smaller than its share hands the slack back, and the caller's `total` still
 * reports the true count so the strip can say "+15 more".
 */
export function capGapsByVariant(
  gaps: readonly RaidDexGap[],
  limit: number,
): RaidDexGap[] {
  if (limit < 0 || gaps.length <= limit) return sortGapsByVariant(gaps);
  if (limit === 0) return [];
  const groups = groupGapsByVariant(gaps);
  const take = groups.map(() => 0);
  let remaining = limit;
  // Stops when the budget is spent or every group is exhausted; `progress`
  // guards the case where the groups run dry before the budget does.
  for (let progress = true; remaining > 0 && progress; ) {
    progress = false;
    for (let i = 0; i < groups.length && remaining > 0; i++) {
      if (take[i] < groups[i].gaps.length) {
        take[i]++;
        remaining--;
        progress = true;
      }
    }
  }
  return groups.flatMap((g, i) => g.gaps.slice(0, take[i]));
}

/** One block of tiles as a gap strip draws it: an optional heading, and its gaps. */
export interface GapDisplayGroup {
  /** The heading to draw, or null for a block that carries none. */
  variant: RaidDexVariant | null;
  gaps: RaidDexGap[];
}

/**
 * How a gap strip should be laid out: grouped under variant headings, or as one
 * flat row.
 *
 * A HEADING PER VARIANT IS ONLY WORTH ITS SPACE WHEN THERE IS SOMETHING TO
 * SORT. An event that features one Pokémon produces one tile per variant of the
 * same species, so the strip became a column of single-tile blocks — "100%",
 * one Groudon; "XXS", one Groudon; "XXL", one Groudon — three headings and three
 * rows to say something every tile's own badge already said. Reported on device
 * 2026-08-12 on a Groudon Raid Hour.
 *
 * So the headings appear only when the strip is showing more than one species,
 * which is the case they were introduced for: a dozen tiles in spawn order,
 * where a species' main and shiny sat side by side and the strip read as noise.
 * With one species the tiles go on one line and the badges do the labelling.
 */
export function gapDisplayGroups(gaps: readonly RaidDexGap[]): GapDisplayGroup[] {
  if (gaps.length === 0) return [];
  const species = new Set(gaps.map((g) => g.speciesId));
  if (species.size <= 1) return [{ variant: null, gaps: sortGapsByVariant(gaps) }];
  return groupGapsByVariant(gaps);
}

/** Short label for a gap's variant, used on the picture-button badge. */
export function raidVariantLabel(v: RaidDexVariant): string {
  switch (v) {
    case "pokemon": return "Dex";
    case "shiny": return "Shiny";
    case "lucky": return "Lucky";
    case "perfect": return "100%";
    case "xxl": return "XXL";
    case "xxs": return "XXS";
    case "mega": return "Mega";
    case "gmax": return "G-Max";
    case "dynamax": return "D-MAX";
    case "shadow": return "Shadow";
    case "purified": return "Purified";
  }
}
